import { memo, useState } from 'react';
import { isString } from 'radash';
import { useClipFitFill } from '../useClipFitFill';
import Toggle from '@/components/atoms/Toggle';
import Icon from '@/components/atoms/Icon';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import { classnames } from '@/libs/utils';
import {
  SCREENSHARE_TRACK_NAME,
  SHARED_VIDEO_TRACK_NAME,
  type SpeakerTimelineTrack
} from '@/stores/contentMetaDataStore/utils/sceneTracks';

function ToggleRow({
  label,
  value,
  onChange,
  disabled = false
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className={classnames('flex items-center justify-between border-b py-3', disabled ? 'opacity-50' : '')}>
      <span className="text-sm">{label}</span>
      <Toggle size="medium" on={value} onToggle={onChange} disabled={disabled} />
    </div>
  );
}

function ScreenIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="4" width="22" height="15" rx="2" stroke="#374151" strokeWidth="1.5" />
      <path d="M10 23h8M14 19v4" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function EyeIcon({ faded = false }: { faded?: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke={faded ? '#9CA3AF' : '#111827'}
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#9CA3AF"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function ParticipantRow({ track, onToggle }: { track: SpeakerTimelineTrack; onToggle: () => void }) {
  const { name, color, selected } = track;
  const isScreen = name === SCREENSHARE_TRACK_NAME || name === SHARED_VIDEO_TRACK_NAME;
  return (
    <div className={classnames('flex items-center justify-between py-2', !selected ? 'opacity-40' : '')}>
      <div className="flex items-center gap-2.5">
        {isScreen ? <ScreenIcon /> : <Icon name="IconUserSquare" size={20} stroke={1.5} className={color} />}
        <span className={classnames('text-sm font-medium', selected && !isScreen ? color : 'text-gray-700')}>
          {name}
        </span>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center justify-center rounded p-0.5 hover:bg-slate-100"
      >
        {selected ? <EyeIcon /> : <EyeOffIcon />}
      </button>
    </div>
  );
}

function ClipToggleOptions({
  showNameCard,
  isSpeakerLabelOn,
  onNameCardToggle,
  showBorder,
  onBorderToggle,
  isBorderDisabled,
  isFitApplied,
  fitFillDisabledReason,
  disabledSpeakerLabelToggle,
  tracks,
  onToggleTrack
}: {
  showNameCard: boolean;
  isSpeakerLabelOn: boolean;
  onNameCardToggle: (value: boolean) => void;
  showBorder: boolean;
  onBorderToggle: (value: boolean) => void;
  isBorderDisabled: boolean;
  isFitApplied: boolean;
  fitFillDisabledReason: string | null;
  disabledSpeakerLabelToggle: boolean;
  tracks: SpeakerTimelineTrack[];
  onToggleTrack: (key: string) => void;
}) {
  const isFitFillDisabled = isString(fitFillDisabledReason);
  const { isOpen, handleCancel, handleConfirm, changeFitMode } = useClipFitFill({
    fitOption: isFitApplied ? 'FILL' : 'FIT'
  });

  const [roundCorners, setRoundCorners] = useState(true);

  return (
    <>
      <div className="flex flex-col gap-3 py-2">
        <ToggleRow
          label="Keep speaker aspect ratio"
          value={isFitApplied}
          onChange={changeFitMode}
          disabled={isFitFillDisabled}
        />
        <ToggleRow label="Show borders" value={showBorder} onChange={onBorderToggle} disabled={isBorderDisabled} />
        <ToggleRow label="Round corners" value={roundCorners} onChange={setRoundCorners} />
        <ToggleRow
          label="Show name cards"
          value={isSpeakerLabelOn}
          onChange={onNameCardToggle}
          disabled={disabledSpeakerLabelToggle || !showNameCard}
        />
      </div>

      <div className="mt-3 pt-3">
        <div className="mb-2 text-xs text-violet-500">Visible participants &amp; screens</div>
        {tracks.map(track => (
          <ParticipantRow key={track.id} track={track} onToggle={() => onToggleTrack(track.toggleKey)} />
        ))}
      </div>

      <ConfirmationDialog
        isOpen={isOpen}
        title="Confirm Fit Change"
        content="This will update all existing scenes with your current selection. Do you want to continue?"
        confirmLabel="Continue"
        cancelLabel="Cancel"
        onClose={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
}

export default memo(ClipToggleOptions);
